import React, { useState, useMemo } from 'react'
import { Dialog } from '@progress/kendo-react-dialogs'
import { cn } from '@utils/mergeClassNames'

import DialogBarImage from '../../../../public/assets/heartbeat/dialog_bar.svg'
import CHROME_OFF from '../../../../public/assets/heartbeat/chrome_off.svg'
import CHROME_UNINSTALL_ONLY from '../../../../public/assets/heartbeat/chrome_uninstall_only.svg'
import CHROME_UNINSTALL_BOTH from '../../../../public/assets/heartbeat/chrome_uninstall_both.svg'

import EDGE_OFF from '../../../../public/assets/heartbeat/edge_off.svg'
import EDGE_UNINSTALL_ONLY from '../../../../public/assets/heartbeat/edge_uninstall_only.svg'
import EDGE_UNINSTALL_BOTH from '../../../../public/assets/heartbeat/edge_uninstall_both.svg'

import DESKTOP_AGENT_OFF from '../../../../public/assets/heartbeat/desktop_agent_off.svg'
import DESKTOP_AGENT_UNINSTALL from '../../../../public/assets/heartbeat/desktop_agent_uninstall.svg'

import AddOnImage from '../../../../public/assets/heartbeat/add-ons.svg'
import { useDeviceStore } from '../../../stores/useDeviceStore'

const descriptionClassName =
  'm-0 text-left text-xs font-normal leading-5 text-[#666]'

const statusIconClassName = 'inline-block shrink-0 align-middle'

const HeartBeatDialog = ({
  visible,
  onClose,
  agentEnabled,
  desktopAgentStatus,
  chromeExtensionStatus,
  edgeExtensionStatus,
}) => {
  const [showMore, setShowMore] = useState(false)
  const isMobile = useDeviceStore(state => state.isMobile)

  // =========================
  // ✅ 统一 desktop 状态
  // =========================
  const desktopStatus = useMemo(() => {
    if (!agentEnabled) return 'online'
    return desktopAgentStatus
  }, [agentEnabled, desktopAgentStatus])

  const state = {
    agentEnabled,
    desktopStatus,
    chrome: chromeExtensionStatus,
    edge: edgeExtensionStatus,
  }

  // =========================
  // ✅ 映射（icon + name）
  // =========================
  const ENTITY_MAP = {
    desktop: {
      name: 'Desktop Agent',
      icon: state => {
        if (!state.agentEnabled) {
          return (
            <img
              className={statusIconClassName}
              src={DESKTOP_AGENT_OFF}
              alt=""
            />
          )
        }

        if (state.desktopStatus === 'uninstall') {
          return (
            <img
              className={statusIconClassName}
              src={DESKTOP_AGENT_UNINSTALL}
              alt=""
            />
          )
        }

        return (
          <img className={statusIconClassName} src={DESKTOP_AGENT_OFF} alt="" />
        )
      },
    },

    chrome: {
      name: 'Chrome Extension',
      icon: state => {
        if (state.chrome === 'uninstall' && state.edge === 'uninstall') {
          return (
            <img
              className={statusIconClassName}
              src={CHROME_UNINSTALL_BOTH}
              alt=""
            />
          )
        }

        if (state.chrome === 'uninstall') {
          return (
            <img
              className={statusIconClassName}
              src={CHROME_UNINSTALL_ONLY}
              alt=""
            />
          )
        }

        return <img className={statusIconClassName} src={CHROME_OFF} alt="" />
      },
    },

    edge: {
      name: 'Edge Extension',
      icon: state => {
        if (state.chrome === 'uninstall' && state.edge === 'uninstall') {
          return (
            <img
              className={statusIconClassName}
              src={EDGE_UNINSTALL_BOTH}
              alt=""
            />
          )
        }

        if (state.edge === 'uninstall') {
          return (
            <img
              className={statusIconClassName}
              src={EDGE_UNINSTALL_ONLY}
              alt=""
            />
          )
        }

        return <img className={statusIconClassName} src={EDGE_OFF} alt="" />
      },
    },
  }

  const renderEntityName = (entity, label = ENTITY_MAP[entity].name) => (
    <span className="inline-flex items-center gap-1 whitespace-nowrap align-middle">
      {ENTITY_MAP[entity].icon(state)}
      <span>{label}</span>
    </span>
  )

  const renderWebExtensionName = () => (
    <span className="inline-flex items-center gap-1 whitespace-nowrap align-middle">
      {ENTITY_MAP['chrome'].icon(state)}
      {ENTITY_MAP['edge'].icon(state)}
      <span>Web Extension</span>
    </span>
  )

  // =========================
  // ✅ 描述（统一入口🔥）
  // =========================
  const getDescription = () => {
    if (
      desktopStatus === 'uninstall' &&
      chromeExtensionStatus === 'uninstall' &&
      edgeExtensionStatus === 'uninstall'
    ) {
      return (
        <>
          It looks like the {renderWebExtensionName()} and{' '}
          {renderEntityName('desktop')} may not be enabled yet. To ensure your
          time is captured accurately, please install and turn on both as soon
          as possible.
        </>
      )
    }

    if (
      desktopStatus === 'uninstall' &&
      (chromeExtensionStatus === 'online' || edgeExtensionStatus === 'online')
    ) {
      return (
        <>
          It looks like the {renderEntityName('desktop')} may not be enabled
          yet. To ensure your time is captured accurately, please install and
          turn on both as soon as possible.
        </>
      )
    }

    if (
      desktopStatus === 'online' &&
      chromeExtensionStatus === 'uninstall' &&
      edgeExtensionStatus === 'uninstall'
    ) {
      return (
        <>
          It looks like the {renderWebExtensionName()} may not be enabled yet.
          To ensure your time is captured accurately, please install and turn on
          both as soon as possible.
        </>
      )
    }

    if (
      desktopStatus === 'offline' &&
      chromeExtensionStatus === 'uninstall' &&
      edgeExtensionStatus === 'uninstall'
    ) {
      return (
        <>
          It looks like the {renderWebExtensionName()} may not be enabled yet,
          and the {renderEntityName('desktop')} is not turned on. To ensure your
          time is captured accurately, please install and turn them on as soon
          as possible.
        </>
      )
    }

    if (
      desktopStatus === 'uninstall' &&
      chromeExtensionStatus === 'offline' &&
      edgeExtensionStatus === 'uninstall'
    ) {
      return (
        <>
          It looks like the {renderEntityName('desktop')} may not be enabled
          yet, and the {renderEntityName('chrome')} is not turned on. To ensure
          your time is captured accurately, please install and turn on both as
          soon as possible.
        </>
      )
    }

    if (
      desktopStatus === 'uninstall' &&
      chromeExtensionStatus === 'uninstall' &&
      edgeExtensionStatus === 'offline'
    ) {
      return (
        <>
          It looks like the {renderEntityName('desktop')} may not be enabled
          yet, and the {renderEntityName('edge')} is not turned on. To ensure
          your time is captured accurately, please install and turn on both as
          soon as possible.
        </>
      )
    }

    if (
      desktopStatus === 'uninstall' &&
      chromeExtensionStatus === 'offline' &&
      edgeExtensionStatus === 'offline'
    ) {
      return (
        <>
          It looks like the {renderEntityName('desktop')} may not be enabled
          yet, and the {renderEntityName('chrome', 'Chrome')} and{' '}
          {renderEntityName('edge')} are not turned on. To ensure your time is
          captured accurately, please install and turn them on as soon as
          possible.
        </>
      )
    }

    if (
      desktopStatus === 'offline' &&
      chromeExtensionStatus === 'offline' &&
      edgeExtensionStatus === 'offline'
    ) {
      return (
        <>
          It looks like the {renderEntityName('desktop')},{' '}
          {renderEntityName('chrome')} and {renderEntityName('edge')} are not
          turned on. To ensure your time is captured accurately, please turn
          them on as soon as possible.
        </>
      )
    }

    if (
      desktopStatus === 'offline' &&
      chromeExtensionStatus === 'uninstall' &&
      edgeExtensionStatus === 'offline'
    ) {
      return (
        <>
          It looks like the {renderEntityName('desktop')} and{' '}
          {renderEntityName('edge')} are not turned on. To ensure your time is
          captured accurately, please turn them on as soon as possible.
        </>
      )
    }

    if (
      desktopStatus === 'offline' &&
      chromeExtensionStatus === 'offline' &&
      edgeExtensionStatus === 'uninstall'
    ) {
      return (
        <>
          It looks like the {renderEntityName('desktop')} and{' '}
          {renderEntityName('chrome')} are not turned on. To ensure your time is
          captured accurately, please turn them on as soon as possible.
        </>
      )
    }

    if (
      desktopStatus === 'online' &&
      chromeExtensionStatus === 'offline' &&
      edgeExtensionStatus === 'offline'
    ) {
      return (
        <>
          It looks like the {renderEntityName('chrome', 'Chrome')} and{' '}
          {renderEntityName('edge')} are not turned on. To ensure your time is
          captured accurately, please turn them on as soon as possible.
        </>
      )
    }

    if (
      desktopStatus === 'offline' &&
      (chromeExtensionStatus === 'online' || edgeExtensionStatus === 'online')
    ) {
      return (
        <>
          It looks like the {renderEntityName('desktop')} is not turned on. To
          ensure your time is captured accurately, please turn it on as soon as
          possible.
        </>
      )
    }

    if (
      desktopStatus === 'online' &&
      chromeExtensionStatus === 'offline' &&
      edgeExtensionStatus === 'uninstall'
    ) {
      return (
        <>
          It looks like the {renderEntityName('chrome')} is not turned on. To
          ensure your time is captured accurately, please turn it on as soon as
          possible.
        </>
      )
    }

    if (
      desktopStatus === 'online' &&
      chromeExtensionStatus === 'uninstall' &&
      edgeExtensionStatus === 'offline'
    ) {
      return (
        <>
          It looks like the {renderEntityName('edge')} is not turned on. To
          ensure your time is captured accurately, please turn it on as soon as
          possible.
        </>
      )
    }

    return null
  }

  // =========================
  // ✅ 标题（简化）
  // =========================
  const getTitle = () => {
    return 'Action Required: Enable MIRA Tracking'
  }

  // =========================
  // ✅ Description 渲染
  // =========================
  const renderDescription = () => {
    const content = getDescription()

    return (
      <>
        <p className={cn(descriptionClassName, 'mb-3')}>{content}</p>

        {!isMobile && (
          <>
            <p className={descriptionClassName}>
              Not installed yet?{' '}
              <span
                className="cursor-pointer text-[#865BDF] hover:underline"
                onClick={() => setShowMore(prev => !prev)}
              >
                {showMore ? 'Show less' : 'Learn more'}
              </span>
            </p>

            {showMore && (
              <p className={descriptionClassName}>
                You can download them from the Settings section in the left
                panel. To access the left panel, click the{' '}
                <img
                  className="inline-block align-middle"
                  src={AddOnImage}
                  alt=""
                />{' '}
                in the top-left corner of MIRA.
              </p>
            )}
          </>
        )}
      </>
    )
  }

  if (!visible) return null

  return (
    <Dialog
      onClose={onClose}
      className={cn(
        '!z-[1000000]',
        '[&_.k-dialog]:w-[480px] [&_.k-dialog]:overflow-hidden [&_.k-dialog]:rounded-2xl [&_.k-dialog]:p-0',
        isMobile && '[&_.k-dialog]:!w-[90%]'
      )}
    >
      <div className="flex flex-col">
        <div>
          <img className="block w-full" src={DialogBarImage} alt="" />
        </div>

        <div
          className={cn(
            'px-[30px] py-[30px] text-center',
            isMobile && 'text-left'
          )}
        >
          <h2 className="m-0 mb-3 text-lg font-semibold text-black">
            {getTitle()}
          </h2>
          {renderDescription()}
        </div>

        <div
          className={cn(
            'flex justify-end border-t border-solid border-[#dadada] px-[30px] py-[15px]',
            isMobile && 'px-5 py-4'
          )}
        >
          <button
            className={cn(
              'cursor-pointer rounded-full border border-solid border-[#525252] bg-[#525252] px-3 py-[5px] text-sm font-normal text-white hover:bg-[#333333]',
              isMobile && 'px-10 py-3.5 text-base leading-5'
            )}
            onClick={onClose}
          >
            Ok
          </button>
        </div>
      </div>
    </Dialog>
  )
}

export default HeartBeatDialog
